import { notEmpty, type Optional } from '@festina-life/core'
import { Button, ButtonContainer, IconButton } from '@festina-life/ui/actions'
import { useTranslate } from '@festina-life/ui/hooks'
import { paperStyles } from '@festina-life/ui/libs'
import { ActionDisclosure } from '@festina-life/ui/listsAndTables'
import { autoUpdate, useFloating } from '@floating-ui/react'
import cx from 'classnames'
import { cloneElement, type MouseEvent, useState } from 'react'

import {
  type ChildAction,
  type ChildActionQuestion,
  isChildActionQuestion,
  type RowActions,
  type StackableType,
} from './types'

type Props<D> = {
  data: StackableType<D>
  actions: RowActions<D>
}

type ActionQuestion<D> = {
  action: ChildAction<D>['action']
  question: ChildActionQuestion
}

export const StackableTableRowActions = <D,>({ data, actions }: Props<D>) => {
  const { refs, floatingStyles } = useFloating({
    whileElementsMounted: autoUpdate,
    placement: 'left-end',
  })
  const [actionQuestion, setActionQuestion] =
    useState<Optional<ActionQuestion<D>>>(null)
  const translate = useTranslate()

  const actionButtons = actions
    .map(({ action, actionId, node, test, title }) => {
      const renderedNode = typeof node === 'function' ? node(data) : node

      if (!renderedNode) {
        return
      }

      if (!action) {
        // If there's no action defined we simply let the node control its own click behavior
        return cloneElement(renderedNode, {
          key: actionId,
          title,
        })
      }

      return cloneElement(renderedNode, {
        key: actionId,
        title,
        onClick: async (evt) => {
          evt.stopPropagation()

          const testResponse = test?.(data)

          if (isChildActionQuestion(testResponse)) {
            setActionQuestion({ question: testResponse, action })
          } else {
            await action?.(data)
          }
        },
      })
    })
    .filter(notEmpty)

  const handleAcceptAction = async (event: MouseEvent) => {
    event.stopPropagation()
    await actionQuestion?.action?.(data)

    setActionQuestion(null)
  }

  const handleCancelAction = (event: MouseEvent) => {
    event.stopPropagation()

    setActionQuestion(null)
  }

  return (
    <td
      className="absolute right-0 flex h-full translate-x-full flex-col"
      ref={refs.setReference}
    >
      {actionQuestion && (
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          className="z-10 flex flex-col gap-y-2 rounded-md bg-slate-100 p-2 shadow-sm dark:bg-slate-800"
        >
          <div>{actionQuestion.question}</div>
          <ButtonContainer className="flex-nowrap!">
            <Button variant="discreet" onClick={handleCancelAction}>
              {translate('no')}
            </Button>
            <Button variant="primary" onClick={handleAcceptAction}>
              {translate('yes')}
            </Button>
          </ButtonContainer>
        </div>
      )}
      <div className="invisible m-1 flex h-full flex-col justify-center overflow-visible text-gray-500 group-hover:visible">
        {!!actionButtons.length && (
          <ActionDisclosure
            // Force a remount when the number of action buttons changes
            key={actionButtons.length}
            isContrast
            variant="horizontal"
            grow="left"
            trigger={
              <IconButton
                tip={null}
                iconName="Wrench"
                className={cx('rounded-full!', paperStyles({ isFaded: true }))}
              />
            }
          >
            {actionButtons}
          </ActionDisclosure>
        )}
      </div>
    </td>
  )
}
